import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { ListPreferencesClient } from './list-preferences-client.service';
import { Observable, of } from 'rxjs';

const PREFERENCES_KEY = '_mp';
const COLUMNS = '_c';
const ORDER_BY = '_o';

export declare type ListPreferences = {
  id: number;
  columns: number[];
  orderBy: number;
};

@Injectable()
export class UserPreferencesService {
  constructor(
    private _client: ListPreferencesClient,
    private _storage: StorageService
  ) {}

  private readonly _listCache: Map<number, { columns: number[]; orderBy: number }> = new Map();
  private readonly _listRequests: Map<number, Observable<ListPreferences>> = new Map();
  private readonly _setRequests: Map<string, Observable<void>> = new Map();

  getListPreferences(id: number): Observable<ListPreferences> {
    if (this._listCache.has(id)) {
      const pref = this._listCache.get(id);
      return of({ id: id, columns: pref.columns, orderBy: pref.orderBy });
    }

    if (this._listRequests.has(id)) {
      return this._listRequests.get(id);
    }

    const request = this._client.get(id).pipe(
      map(res => {
        if (res) {
          this._listCache.set(id, { columns: res.columns, orderBy: res.orderBy });
          return { id: res.id, columns: res.columns, orderBy: res.orderBy };
        }
        return { id: id, columns: [], orderBy: 0 };
      }),
      finalize(() => this._listRequests.delete(id)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this._listRequests.set(id, request);
    return request;
  }

  setListPreferences(preferences: ListPreferences): Observable<void> {
    const requestKey = `${preferences.id}:${preferences.columns.join(',')}:${preferences.orderBy}`;
    if (this._setRequests.has(requestKey)) {
      return this._setRequests.get(requestKey);
    }

    const updateCache = () => {
      this._listCache.set(preferences.id, {
        columns: preferences.columns,
        orderBy: preferences.orderBy,
      });
    };

    let request: Observable<void>;
    if (this._listCache.has(preferences.id)) {
      request = this._client.update(preferences).pipe(tap(updateCache));
    } else {
      request = this._client.create(preferences).pipe(tap(updateCache));
    }

    request = request.pipe(
      finalize(() => this._setRequests.delete(requestKey)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this._setRequests.set(requestKey, request);
    return request;
  }
}
